import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axiosClient from '../../services/axiosClient';

// Fonction utilitaire pour lire le token du localStorage
function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) {
			return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		}
		return {};
	} catch (_) {
		return {};
	}
}

const SalaryTypes = () => {
	const [salaryTypes, setSalaryTypes] = useState([]);
	const [filteredSalaryTypes, setFilteredSalaryTypes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedSalaryTypes, setSelectedSalaryTypes] = useState([]);
	const [showAddModal, setShowAddModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [currentSalaryType, setCurrentSalaryType] = useState(null);
	const [recordsPerPage, setRecordsPerPage] = useState(25);
	const [formData, setFormData] = useState({
		name: '',
		active: 1
	});

	// Fetch salary types from API
	useEffect(() => {
		fetchSalaryTypes();
	}, []);

	const fetchSalaryTypes = async () => {
		try {
			setLoading(true);
			console.log('[SalaryTypes] Fetching from /salaryTypes...');
			const response = await axiosClient.get('/salaryTypes', {
				headers: { 
					'Accept': 'application/json',
					...getAuthHeader() 
				},
			});
			console.log('[SalaryTypes] API Response:', response);
			
			let data = response?.data;
			let list = data?.result?.data || data?.data || data?.result || data || [];
			if (!Array.isArray(list)) {
				if (list && Array.isArray(list.data)) list = list.data;
				else list = [];
			}
			
			// Normaliser les données
			const normalizedSalaryTypes = list.map(st => ({
				id: st.id || st.salary_type_id,
				name: st.name || st.title || st.label,
				active: st.active !== undefined ? st.active : 1,
				translation: st.translation
			}));
			
			console.log('[SalaryTypes] Parsed salary types:', normalizedSalaryTypes);
			setSalaryTypes(normalizedSalaryTypes);
			setFilteredSalaryTypes(normalizedSalaryTypes);
		} catch (err) {
			console.error('[SalaryTypes] Error fetching salary types:', err);
		} finally {
			setLoading(false);
		}
	};

	// Filter salary types based on search
	useEffect(() => {
		let filtered = [...salaryTypes];

		if (searchTerm) {
			filtered = filtered.filter(st =>
				st.name?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		setFilteredSalaryTypes(filtered);
	}, [searchTerm, salaryTypes]);

	// Handlers
	const handleAddSalaryType = async () => {
		try {
			const response = await axiosClient.post('/salaryTypes', formData, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 201) {
				await fetchSalaryTypes();
				setShowAddModal(false);
				setFormData({ name: '', active: 1 });
			}
		} catch (err) {
			console.error('Error adding salary type:', err);
			alert('Erreur lors de l\'ajout du type de salaire');
		}
	};

	const handleEditSalaryType = (salaryType) => {
		setCurrentSalaryType(salaryType);
		setFormData({
			name: salaryType.name,
			active: salaryType.active
		});
		setShowEditModal(true);
	};

	const handleUpdateSalaryType = async () => {
		try {
			const response = await axiosClient.put(
				`/salaryTypes/${currentSalaryType.id}`, 
				formData,
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchSalaryTypes();
				setShowEditModal(false);
				setCurrentSalaryType(null);
				setFormData({ name: '', active: 1 });
			}
		} catch (err) {
			console.error('Error updating salary type:', err);
			alert('Erreur lors de la mise à jour du type de salaire');
		}
	};

	const handleDeleteSalaryType = async (id) => {
		if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type de salaire ?')) {
			return;
		}
		
		try {
			const response = await axiosClient.delete(`/salaryTypes/${id}`, {
				headers: { 
					'Accept': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 204) {
				await fetchSalaryTypes();
			}
		} catch (err) {
			console.error('Error deleting salary type:', err);
			alert('Erreur lors de la suppression du type de salaire');
		}
	};

	const handleDeleteSelected = async () => {
		if (!window.confirm(`Supprimer ${selectedSalaryTypes.length} type(s) de salaire ?`)) {
			return;
		}
		
		try {
			for (const id of selectedSalaryTypes) {
				await axiosClient.delete(`/salaryTypes/${id}`, {
					headers: { 
						'Accept': 'application/json',
						...getAuthHeader() 
					},
				});
			}
			await fetchSalaryTypes();
			setSelectedSalaryTypes([]);
		} catch (err) {
			console.error('Error deleting salary types:', err);
			alert('Erreur lors de la suppression des types de salaire');
		}
	};

	const toggleActive = async (salaryType) => {
		try {
			const response = await axiosClient.put(
				`/salaryTypes/${salaryType.id}`,
				{ ...salaryType, active: salaryType.active === 1 ? 0 : 1 },
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchSalaryTypes();
			}
		} catch (err) {
			console.error('Error toggling active status:', err);
		}
	};

	return (
		<>
			{/* Header */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center flex-wrap">
						<div>
							<h2 className="mb-1" style={{ fontSize: '28px', fontWeight: '700' }}>Types De Salaire</h2>
							<p className="text-muted mb-0">
								Affichage des éléments 1 à {filteredSalaryTypes.length} sur {filteredSalaryTypes.length}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Help Section */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="alert alert-info" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#e7f3ff' }}>
						<div className="d-flex align-items-start">
							<i className="fas fa-info-circle me-3 mt-1" style={{ fontSize: '20px', color: '#0066cc' }}></i>
							<div>
								<strong>Help</strong>
								<p className="mb-0 mt-2" style={{ fontSize: '14px', lineHeight: '1.6' }}>
									To translate this list you have to select a language by clicking on the dropdown button <span className="badge bg-primary">🌐</span> icon located at the right of each <span className="badge bg-success">✏ Edit</span> button. Translatable fields can be identified by the <span className="badge bg-secondary">🌐</span> icon before or after their label in the form.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex gap-2 flex-wrap">
						<button 
							className="btn btn-primary"
							onClick={() => setShowAddModal(true)}
							style={{ borderRadius: '8px', fontWeight: '600' }}
						>
							<i className="fas fa-plus me-2"></i>Add Salary Type
						</button>
						<button 
							className="btn btn-secondary"
							style={{ borderRadius: '8px', fontWeight: '600' }}
						>
							<i className="fas fa-sort me-2"></i>Reorder Salary Types
						</button>
						<button 
							className="btn btn-danger"
							disabled={selectedSalaryTypes.length === 0}
							onClick={handleDeleteSelected}
							style={{ borderRadius: '8px', fontWeight: '600' }}
						>
							<i className="fas fa-times me-2"></i>Delete
						</button>
						<button 
							className="btn btn-info"
							onClick={fetchSalaryTypes}
							style={{ borderRadius: '8px', fontWeight: '600' }}
						>
							<i className="fas fa-sync me-2"></i>Rebuild Salary Types Nodes
						</button>
					</div>
				</div>
			</div>

			{/* Records per page and Search */}
			<div className="row mb-3">
				<div className="col-md-6">
					<div className="d-flex align-items-center">
						<select 
							className="form-select" 
							style={{ width: '100px', borderRadius: '8px' }}
							value={recordsPerPage}
							onChange={(e) => setRecordsPerPage(Number(e.target.value))}
						>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
						<span className="ms-3 text-muted">records per page</span>
					</div>
				</div>
				<div className="col-md-6">
					<div className="d-flex justify-content-end">
						<div className="input-group" style={{ maxWidth: '300px' }}>
							<span className="input-group-text bg-white" style={{ borderRadius: '8px 0 0 8px' }}>
								Search:
							</span>
							<input 
								type="text" 
								className="form-control" 
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								style={{ borderRadius: '0 8px 8px 0' }}
								placeholder="Search salary types..."
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="row">
				<div className="col-12">
					<div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
						<div className="card-body p-0">
							{loading ? (
								<div className="text-center py-5">
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</div>
							) : filteredSalaryTypes.length === 0 ? (
								<div className="text-center py-5">
									<p className="text-muted">Aucun type de salaire trouvé</p>
								</div>
							) : (
								<div className="table-responsive">
									<table className="table table-hover mb-0">
										<thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
											<tr>
												<th style={{ padding: '16px', width: '50px' }}>
													<input 
														type="checkbox" 
														className="form-check-input"
														onChange={(e) => {
															if (e.target.checked) {
																setSelectedSalaryTypes(filteredSalaryTypes.map(st => st.id));
															} else {
																setSelectedSalaryTypes([]);
															}
														}}
														checked={selectedSalaryTypes.length === filteredSalaryTypes.length && filteredSalaryTypes.length > 0}
													/>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px' }}>
													<div className="d-flex align-items-center">
														Name
														<i className="fas fa-sort ms-2 text-muted" style={{ fontSize: '12px', cursor: 'pointer' }}></i>
													</div>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
													<div className="d-flex align-items-center justify-content-center">
														Active
														<i className="fas fa-sort ms-2 text-muted" style={{ fontSize: '12px', cursor: 'pointer' }}></i>
													</div>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
													Actions
												</th>
											</tr>
										</thead>
										<tbody>
											{filteredSalaryTypes.map((salaryType) => (
												<tr 
													key={salaryType.id}
													style={{ 
														borderBottom: '1px solid #f0f0f0',
														transition: 'background-color 0.2s'
													}}
													onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
													onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
												>
													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<input 
															type="checkbox" 
															className="form-check-input"
															checked={selectedSalaryTypes.includes(salaryType.id)}
															onChange={() => {
																if (selectedSalaryTypes.includes(salaryType.id)) {
																	setSelectedSalaryTypes(selectedSalaryTypes.filter(id => id !== salaryType.id));
																} else {
																	setSelectedSalaryTypes([...selectedSalaryTypes, salaryType.id]);
																}
															}}
														/>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<span style={{ fontSize: '15px', fontWeight: '500' }}>
															{salaryType.name}
														</span>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														<div className="form-check form-switch d-flex justify-content-center">
															<input 
																className="form-check-input" 
																type="checkbox" 
																checked={salaryType.active === 1}
																onChange={() => toggleActive(salaryType)}
																style={{ cursor: 'pointer', width: '48px', height: '24px' }}
															/>
														</div>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														<div className="d-flex gap-2 justify-content-center">
															<button 
																className="btn btn-sm btn-primary"
																onClick={() => handleEditSalaryType(salaryType)}
																style={{ borderRadius: '6px', padding: '6px 12px' }}
															>
																<i className="fas fa-edit me-1"></i>Edit
															</button>
															<button 
																className="btn btn-sm btn-danger"
																onClick={() => handleDeleteSalaryType(salaryType.id)}
																style={{ borderRadius: '6px', padding: '6px 12px' }}
															>
																<i className="fas fa-trash me-1"></i>Delete
															</button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Footer Info */}
			<div className="row mt-3">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center">
						<p className="text-muted mb-0" style={{ fontSize: '14px' }}>
							Showing entries 1 to {filteredSalaryTypes.length} of {filteredSalaryTypes.length}.
						</p>
						<div className="d-flex gap-2">
							<button className="btn btn-sm btn-outline-primary" disabled>Previous</button>
							<button className="btn btn-sm btn-primary">1</button>
							<button className="btn btn-sm btn-outline-primary" disabled>Next</button>
						</div>
					</div>
				</div>
			</div>

			{/* Add Modal */}
			<Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
				<Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
					<Modal.Title style={{ fontWeight: '700' }}>Ajouter un type de salaire</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label fw-bold">Nom *</label>
						<input
							type="text"
							className="form-control"
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							placeholder="Ex: Mensuel, Annuel, Horaire"
							style={{ borderRadius: '8px' }}
						/>
					</div>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={formData.active === 1}
							onChange={(e) => setFormData({ ...formData, active: e.target.checked ? 1 : 0 })}
							id="addActiveCheck"
							style={{ width: '48px', height: '24px' }}
						/>
						<label className="form-check-label fw-bold ms-2" htmlFor="addActiveCheck">
							Active
						</label>
					</div>
				</Modal.Body>
				<Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
					<button className="btn btn-secondary" onClick={() => setShowAddModal(false)} style={{ borderRadius: '8px' }}>
						Annuler
					</button>
					<button
						className="btn btn-primary"
						onClick={handleAddSalaryType}
						disabled={!formData.name}
						style={{ borderRadius: '8px' }}
					>
						<i className="fas fa-save me-2"></i>Ajouter
					</button>
				</Modal.Footer>
			</Modal>

			{/* Edit Modal */}
			<Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
				<Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
					<Modal.Title style={{ fontWeight: '700' }}>Modifier le type de salaire</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label fw-bold">Nom *</label>
						<input
							type="text"
							className="form-control"
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							style={{ borderRadius: '8px' }}
						/>
					</div>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={formData.active === 1}
							onChange={(e) => setFormData({ ...formData, active: e.target.checked ? 1 : 0 })}
							id="editActiveCheck"
							style={{ width: '48px', height: '24px' }}
						/>
						<label className="form-check-label fw-bold ms-2" htmlFor="editActiveCheck">
							Active
						</label>
					</div>
				</Modal.Body>
				<Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
					<button className="btn btn-secondary" onClick={() => setShowEditModal(false)} style={{ borderRadius: '8px' }}>
						Annuler
					</button>
					<button
						className="btn btn-primary"
						onClick={handleUpdateSalaryType}
						disabled={!formData.name}
						style={{ borderRadius: '8px' }}
					>
						<i className="fas fa-save me-2"></i>Mettre à jour
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default SalaryTypes;
